/* ── useMapPicker ────────────────────────────────────────────
 * All of MapPicker's state, map lifecycle and network calls.
 * MapPicker.js stays presentation-only.
 * ──────────────────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MapsErrorCode,
  hasApiKey,
  loadMapsLibrary,
  onAuthFailure,
} from './googleMapsLoader';
import {
  createSequencer,
  createSessionToken,
  fetchSuggestions,
  resolveSuggestion,
  reverseGeocode,
  zoomForTypes,
} from './geocoding';
import { stylesForTheme } from './mapTheme';

const SEARCH_DEBOUNCE_MS = 250;
const IDLE_GEOCODE_DEBOUNCE_MS = 350;
const RECENTS_KEY = 'wa.mappicker.recents';
const RECENTS_LIMIT = 3;

const readRecents = () => {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, RECENTS_LIMIT) : [];
  } catch {
    return [];
  }
};

const writeRecents = (list) => {
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(list));
  } catch {
    /* private mode / quota — recents are a convenience, never required */
  }
};

const coordsEqual = (a, b) =>
  a && b && Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;

export const useMapPicker = ({
  value,
  onChange,
  defaultCenter,
  defaultZoom,
  countries,
  region,
  language,
  disabled,
}) => {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);

  /* Consumers pass an inline arrow for onChange, so its identity changes
   * every render. Reading it through a ref keeps `commit` — and therefore
   * the map's event listeners — stable, instead of tearing down and
   * re-attaching every listener on each keystroke. */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Latest committed value, for callbacks that must not close over stale state.
  const locationRef = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  /* User-gesture tracking. Programmatic pans (flying to a search result)
   * must NOT trigger a reverse geocode — otherwise picking a suggestion
   * immediately overwrites itself with whatever the map centre resolves
   * to. Only a real drag arms the idle handler. */
  const userMovedRef = useRef(false);
  const idleTimerRef = useRef(null);
  const searchTimerRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const blurGuardRef = useRef(false);

  const geocodeSeq = useRef(createSequencer()).current;
  const searchSeq = useRef(createSequencer()).current;

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState(null);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [resolving, setResolving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [location, setLocation] = useState(null);
  const [geoState, setGeoState] = useState('idle'); // idle | locating | denied | unavailable
  const [recents, setRecents] = useState(() => readRecents());

  /* ── Commit ────────────────────────────────────────────────
   * The single funnel through which the parent form is updated.
   * Half-typed search text never reaches it — that was the bug that
   * let shop_address and shop_latitude drift apart. */
  const commit = useCallback(
    (next, { remember = false } = {}) => {
      if (!next) return;
      locationRef.current = next;
      setLocation(next);
      setQuery(next.address || '');
      if (remember) {
        setRecents((prev) => {
          const deduped = [
            next,
            ...prev.filter((r) => r.placeId !== next.placeId),
          ].slice(0, RECENTS_LIMIT);
          writeRecents(deduped);
          return deduped;
        });
      }
      onChangeRef.current?.({
        lat: next.lat,
        lng: next.lng,
        address: next.address,
        landmark: next.landmark,
        city: next.city,
        state: next.state,
        country: next.country,
        postalCode: next.postalCode,
      });
    },
    []
  );

  /* ── Map bootstrap ─────────────────────────────────────── */
  useEffect(() => {
    if (!hasApiKey()) {
      setStatus('error');
      setLoadError({
        code: MapsErrorCode.MISSING_KEY,
        message: 'Map key is not configured.',
      });
      return undefined;
    }

    let cancelled = false;
    const offAuth = onAuthFailure(() => {
      if (cancelled) return;
      setStatus('error');
      // gm_authFailure carries no arguments, so we cannot tell
      // ApiNotActivatedMapError from RefererNotAllowedMapError or
      // BillingNotEnabledMapError here — Google only logs the specific
      // code to the console. Name the real causes rather than guessing
      // at one of them.
      setLoadError({
        code: MapsErrorCode.AUTH_FAILED,
        message:
          'Google rejected this map key. Check the browser console for the ' +
          'exact reason, then in Cloud Console enable Maps JavaScript API, ' +
          'Places API (New) and Geocoding API, confirm billing is active, ' +
          'and review the key’s HTTP referrer restrictions.',
      });
    });

    loadMapsLibrary('maps')
      .then(({ Map }) => {
        if (cancelled || !mapNodeRef.current) return;

        const start = {
          lat: Number(value?.lat) || defaultCenter.lat,
          lng: Number(value?.lng) || defaultCenter.lng,
        };

        const map = new Map(mapNodeRef.current, {
          center: start,
          zoom: value?.lat ? defaultZoom : 11,
          disableDefaultUI: true,
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_TOP,
          },
          clickableIcons: true,
          // Greedy: one-finger pan works without a two-finger hint overlay.
          // The map is inside a scrolling form, so we keep scrollwheel zoom
          // off until the user actually interacts with the map.
          gestureHandling: 'greedy',
          scrollwheel: false,
          keyboardShortcuts: true,
          styles: stylesForTheme() || undefined,
        });

        mapRef.current = map;
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setLoadError({
          code: err?.code || MapsErrorCode.LOAD_FAILED,
          message: err?.message || 'The map could not be loaded.',
        });
      });

    return () => {
      cancelled = true;
      offAuth();
    };
    // Bootstrap once. Later `value` changes are handled by the sync effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Re-style on theme flip ────────────────────────────── */
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return undefined;
    const observer = new MutationObserver(() => {
      mapRef.current?.setOptions({ styles: stylesForTheme() });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  /* ── Programmatic move helper ──────────────────────────── */
  const moveTo = useCallback((target, { viewport, types } = {}) => {
    const map = mapRef.current;
    if (!map) return;
    userMovedRef.current = false; // this move is ours, not the user's
    if (viewport) {
      map.fitBounds(viewport);
    } else {
      map.panTo(target);
      map.setZoom(zoomForTypes(types, defaultZoom));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultZoom]);

  /* ── Idle → reverse geocode ────────────────────────────── */
  const runReverseGeocode = useCallback(
    async (coords) => {
      setResolving(true);
      try {
        const next = await geocodeSeq(() =>
          reverseGeocode({ ...coords, language })
        );
        if (next === undefined) return; // superseded by a newer drag
        if (next) commit(next);
      } catch {
        setSearchError('Could not resolve this point. Try again.');
      } finally {
        setResolving(false);
      }
    },
    [commit, geocodeSeq, language]
  );

  useEffect(() => {
    const map = mapRef.current;
    if (status !== 'ready' || !map || disabled) return undefined;

    const onDragStart = () => {
      userMovedRef.current = true;
      setDragging(true);
      setOpen(false);
    };

    const onIdle = () => {
      setDragging(false);
      if (!userMovedRef.current) return; // our own pan — ignore
      userMovedRef.current = false;
      const c = map.getCenter();
      const coords = { lat: c.lat(), lng: c.lng() };
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(
        () => runReverseGeocode(coords),
        IDLE_GEOCODE_DEBOUNCE_MS
      );
    };

    // Click-to-place: tapping a spot is faster than dragging to it.
    const onClick = (e) => {
      if (!e.latLng) return;
      const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      moveTo(coords);
      runReverseGeocode(coords);
    };

    // Scrollwheel zoom stays off until the map is engaged, so scrolling
    // the form past the map doesn't get hijacked.
    const enableScroll = () => map.setOptions({ scrollwheel: true });
    const disableScroll = () => map.setOptions({ scrollwheel: false });

    const listeners = [
      map.addListener('dragstart', onDragStart),
      map.addListener('idle', onIdle),
      map.addListener('click', onClick),
      map.addListener('mouseover', enableScroll),
      map.addListener('mouseout', disableScroll),
    ];

    return () => {
      listeners.forEach((l) => l.remove());
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [status, disabled, runReverseGeocode, moveTo]);

  /* ── Sync from parent (edit mode load / external reset) ── */
  useEffect(() => {
    const lat = Number(value?.lat);
    const lng = Number(value?.lng);
    if (!lat || !lng) return;
    if (coordsEqual(location, { lat, lng })) return;

    const incoming = {
      lat,
      lng,
      address: value?.address || '',
      primaryLine: value?.address || '',
      landmark: value?.landmark || '',
      city: value?.city || '',
      state: value?.state || '',
      country: value?.country || '',
      postalCode: value?.postalCode || '',
      placeId: '',
    };
    locationRef.current = incoming;
    setLocation(incoming);
    setQuery(incoming.address);
    moveTo({ lat, lng });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng, status]);

  /* ── Autocomplete ──────────────────────────────────────── */
  const runSearch = useCallback(
    async (text) => {
      if (!text || text.trim().length < 3) {
        setSuggestions([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      setSearchError('');
      try {
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = await createSessionToken();
        }
        // Bias toward what the user is currently looking at, so "main
        // street" means the one nearby rather than one on another
        // continent. This is the biggest single quality win in search.
        const bounds = mapRef.current?.getBounds?.() || null;

        const results = await searchSeq(() =>
          fetchSuggestions({
            input: text,
            sessionToken: sessionTokenRef.current,
            locationBias: bounds || undefined,
            region,
            countries,
          })
        );
        if (results === undefined) return; // superseded
        setSuggestions(results);
        setActiveIndex(results.length ? 0 : -1);
        setOpen(true);
      } catch {
        setSuggestions([]);
        setSearchError('Address search is unavailable right now.');
        setOpen(true);
      } finally {
        setSearching(false);
      }
    },
    [countries, region, searchSeq]
  );

  const handleQueryChange = useCallback(
    (e) => {
      const text = e.target.value;
      setQuery(text); // local only — never touches the form
      setSearchError('');
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (text.trim().length < 3) {
        setSuggestions([]);
        setOpen(text.trim().length === 0 && recents.length > 0);
        return;
      }
      searchTimerRef.current = setTimeout(
        () => runSearch(text),
        SEARCH_DEBOUNCE_MS
      );
    },
    [runSearch, recents.length]
  );

  const selectSuggestion = useCallback(
    async (suggestion) => {
      setOpen(false);
      setSuggestions([]);
      setResolving(true);
      try {
        const next = await resolveSuggestion(suggestion);
        // The session ends when a place is selected; the next keystroke
        // starts a fresh (separately billed) session.
        sessionTokenRef.current = null;
        moveTo(
          { lat: next.lat, lng: next.lng },
          { viewport: next.viewport, types: next.types }
        );
        commit(next, { remember: true });
      } catch {
        setSearchError('Could not load that place. Pick another result.');
      } finally {
        setResolving(false);
      }
    },
    [commit, moveTo]
  );

  const selectRecent = useCallback(
    (recent) => {
      setOpen(false);
      moveTo({ lat: recent.lat, lng: recent.lng });
      commit(recent);
    },
    [commit, moveTo]
  );

  /* ── Keyboard nav on the suggestion list ───────────────── */
  const listItems = useMemo(
    () => (query.trim().length < 3 ? recents : suggestions),
    [query, recents, suggestions]
  );
  const showingRecents = query.trim().length < 3 && recents.length > 0;

  const handleKeyDown = useCallback(
    (e) => {
      if (!open || !listItems.length) {
        if (e.key === 'ArrowDown' && listItems.length) setOpen(true);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % listItems.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + listItems.length) % listItems.length);
      } else if (e.key === 'Enter') {
        if (activeIndex < 0) return;
        e.preventDefault();
        const item = listItems[activeIndex];
        if (showingRecents) selectRecent(item);
        else selectSuggestion(item);
      } else if (e.key === 'Escape') {
        setOpen(false);
        setActiveIndex(-1);
      }
    },
    [open, listItems, activeIndex, showingRecents, selectRecent, selectSuggestion]
  );

  const handleFocus = useCallback(() => {
    if (query.trim().length < 3 && recents.length) setOpen(true);
    else if (suggestions.length) setOpen(true);
  }, [query, recents.length, suggestions.length]);

  /* A ref guard beats the old 200ms blur timeout: the list sets the guard
   * on pointerdown, so blur knows a selection is in progress. */
  const handleBlur = useCallback(() => {
    if (blurGuardRef.current) {
      blurGuardRef.current = false;
      return;
    }
    setOpen(false);
    setActiveIndex(-1);
    // Typed-but-uncommitted text is discarded — the field always shows
    // the address that is actually saved.
    setQuery(location?.address || '');
  }, [location]);

  const armBlurGuard = useCallback(() => {
    blurGuardRef.current = true;
  }, []);

  /* ── Use my current location ───────────────────────────── */
  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoState('unavailable');
      return;
    }
    setGeoState('locating');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoState('idle');
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        moveTo(coords);
        runReverseGeocode(coords);
      },
      (err) => {
        setGeoState(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [moveTo, runReverseGeocode]);

  /* ── Manual edits to the structured fields ─────────────── */
  const setDetailField = useCallback(
    (field) => (e) => {
      const current = locationRef.current || {};
      const fallback = valueRef.current || {};
      const next = { ...current, [field]: e.target.value };

      // Editing "City" before anything has been pinned must not blank the
      // coordinates: fall back to whatever the parent already holds rather
      // than emitting undefined lat/lng.
      const lat = next.lat ?? (Number(fallback.lat) || undefined);
      const lng = next.lng ?? (Number(fallback.lng) || undefined);
      next.lat = lat;
      next.lng = lng;

      locationRef.current = next;
      setLocation(next);
      onChangeRef.current?.({
        lat: lat ?? '',
        lng: lng ?? '',
        address: next.address ?? fallback.address ?? '',
        landmark: next.landmark ?? '',
        city: next.city ?? '',
        state: next.state ?? '',
        country: next.country ?? '',
        postalCode: next.postalCode ?? '',
      });
    },
    []
  );

  /* ── Cleanup ───────────────────────────────────────────── */
  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    },
    []
  );

  return {
    mapNodeRef,

    status,
    loadError,

    query,
    suggestions,
    recents,
    listItems,
    showingRecents,
    activeIndex,
    setActiveIndex,
    open,
    searching,
    searchError,

    resolving,
    dragging,
    location,
    geoState,

    handleQueryChange,
    handleKeyDown,
    handleFocus,
    handleBlur,
    armBlurGuard,
    selectSuggestion,
    selectRecent,
    useMyLocation,
    setDetailField,
  };
};

export default useMapPicker;
